import React, { forwardRef, HTMLAttributeAnchorTarget, MouseEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { CEntity, IEntity } from '@ts/ui-models';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Typography, TTextType } from '@ts/storybook';
import { useTheme } from '@mui/material/styles';

interface ILinkStylesProps {
  disabled?: boolean;
  disabledUnderline?: boolean;
  theme: Theme;
}

export const useLinkStyles = makeStyles<Theme, ILinkStylesProps>(theme => ({
  link: {
    //color: props => (props.disabled ? '#9e9e9e' : '#1976d2'),
    color: props => props.theme.palette.primary.main,
    cursor: props => (props.disabled ? 'not-allowed' : 'pointer'),
    textDecoration: 'none',
    '&:hover': {
      textDecoration: props => (props.disabled || props.disabledUnderline ? 'none' : 'underline'),
    },
  },
}));

interface ILinkProps {
  href?: string;
  entity?: CEntity<IEntity>; //Extra info for breadcrumb
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  label?: TTextType;
  target?: HTMLAttributeAnchorTarget;
  children?: ReactNode;
  style?: object;
  disabled?: boolean;
  disabledUnderline?: boolean; //To know if the underline is disabled
}

const Link = forwardRef<HTMLAnchorElement, ILinkProps>((props, ref) => {
  const { entity, onClick } = props;
  const labelTEntity = entity?.getLabelEntity();
  const {
    href,
    target,
    children,
    style,
    disabled = Boolean(!labelTEntity && !onClick),
    disabledUnderline = Boolean(!labelTEntity && !onClick),
    label,
  } = props;
  const theme = useTheme();
  const classes = useLinkStyles({ disabled, disabledUnderline, theme });
  const navigate = useNavigate();
  const computedHref = href ? href : entity ? `/${entity?.tEntity}/${entity?.getID()}/view` : '';

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    const titleNewRoute = `${labelTEntity} | ${entity?.toString()}`;

    if (event.ctrlKey || event.metaKey) {
      window.open(computedHref + (entity ? `?title=${titleNewRoute}` : ''), '_blank');
      return;
    }

    if (onClick) {
      event.stopPropagation();
      onClick(event);
    } else if (target !== '_blank') navigate(computedHref, { state: { title: titleNewRoute } });
  };

  return (
    <a
      ref={ref}
      href={disabled || disabledUnderline ? undefined : computedHref}
      target={target}
      onClick={handleClick}
      className={classes.link}
    >
      <Typography sx={{ display: 'inline', ...style }} variant="body2" component="span" text={label ?? children} />
    </a>
  );
});

export default Link;
